import React from "react";
import {
  Button,
  Card,
  Col,
  message,
  Modal,
  Row,
  Space,
  Tabs,
  Typography,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";

import UploadTransactionScheduleExcel from "../components/UploadTransactionScheduleExcel";
import ManualTransactionScheduleForm from "../components/ManualTransactionScheduleForm";
import TransactionScheduleManageModal from "../components/TransactionScheduleManageModal";
import TransactionScheduleFilters from "../components/transactionSchedule/TransactionScheduleFilters";
import TransactionSchedulesTab from "../components/transactionSchedule/TransactionSchedulesTab";
import TransactionScheduleCalendarCard from "../components/transactionSchedule/TransactionScheduleCalendarCard";
import UnmatchedReturnsTab from "../components/transactionSchedule/UnmatchedReturnsTab";
import BulkManagePreviewModal from "../components/transactionSchedule/BulkManagePreviewModal";
import TransactionScheduleDetailDrawer from "../components/transactionSchedule/TransactionScheduleDetailDrawer";
import BatchProgressModal from "../components/common/BatchProgressModal";

import ROUTE_PATHS from "../routes/routePaths";
import useTransactionSchedulePageData from "../hooks/useTransactionSchedulePageData";
import { openBlobInNewTab, saveBlob } from "../components/transactionSchedule/fileActions";
import {
  downloadTransactionScheduleInvoice,
  downloadTransactionScheduleContract,
  downloadGeneratedAllocationSlipPdf,
  downloadGeneratedAllocationSampleFilledPdf,
  uploadTransactionScheduleInvoice,
  uploadTransactionScheduleContract,
} from "../api/transactionScheduleApi";

const { Title, Text } = Typography;

function TransactionSchedulePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    batch,
    canAgree,
    canDelete,
    canUnagree,
    isAuthor,

    filteredTransactions,
    summary,

    loadingTable,
    loadingDetails,
    loadingUnmatchedReturns,
    loadingReturnHistory,
    loadingIgnoredReturns,

    returns,
    returnHistory,
    ignoredReturns,

    drawerOpen,
    selectedRecord,
    detailData,
    statusLogs,

    manageOpen,
    manageRecord,

    runningAllChecks,
    runningCheckIds,

    bulkPreviewOpen,
    bulkRows,
    bulkPayload,

    agreeingAllocationIds,
    unagreeingAllocationIds,

    searchRequestNumber,
    setSearchRequestNumber,
    searchPayer,
    setSearchPayer,
    selectedParentDisplayStatus,
    setSelectedParentDisplayStatus,
    selectedCreatedAtRange,
    setSelectedCreatedAtRange,

    setDrawerOpen,
    setSelectedRecord,
    setManageOpen,
    setBulkPreviewOpen,
    setBulkPayload,

    loadTransactions,
    loadUnmatchedReturns,
    loadReturnHistory,
    loadIgnoredReturns,
    refreshReturnsData,
    openManage,
    openDetails,
    refreshDetails,
    handleAgreeAllocation,
    handleUnagreeAllocation,
    handleRunAllocationCheck,
    handleRunAllChecks,
    handleDeleteTransactionSchedule,
    openBulkPreview,
    handleBulkManage,
    handleBulkAgree,

    initialTab,
  } = useTransactionSchedulePageData(location);

  const [activeTab, setActiveTab] = React.useState(initialTab);
  const [excelImportOpen, setExcelImportOpen] = React.useState(false);

  const lastTabQueryRef = React.useRef(location.search);

  React.useEffect(() => {
    if (lastTabQueryRef.current !== location.search) {
      setActiveTab(initialTab);
      lastTabQueryRef.current = location.search;
    }
  }, [initialTab, location.search]);


  const handleViewInvoice = async (record) => {
    try {
      const file = await downloadTransactionScheduleInvoice(record.id);
      openBlobInNewTab(file);
    } catch (error) {
      message.error(error.message || "Нэхэмжлэх нээж чадсангүй");
    }
  };

  const handleDownloadInvoice = async (record) => {
    try {
      const file = await downloadTransactionScheduleInvoice(record.id);
      saveBlob(file, record.invoiceFileName || `invoice-${record.id}.pdf`);
    } catch (error) {
      message.error(error.message || "Нэхэмжлэх татаж чадсангүй");
    }
  };

  const handleViewContract = async (record) => {
    try {
      const file = await downloadTransactionScheduleContract(record.id);
      openBlobInNewTab(file);
    } catch (error) {
      message.error(error.message || "Гэрээ нээж чадсангүй");
    }
  };

  const handleDownloadContract = async (record) => {
    try {
      const file = await downloadTransactionScheduleContract(record.id);
      saveBlob(file, record.contractFileName || `contract-${record.id}.pdf`);
    } catch (error) {
      message.error(error.message || "Гэрээ татаж чадсангүй");
    }
  };

  const handleViewGeneratedSlipPdf = async (allocation) => {
    try {
      const file = await downloadGeneratedAllocationSlipPdf(allocation.id);
      openBlobInNewTab(file);
    } catch (error) {
      message.error(error.message || "Slip PDF нээж чадсангүй");
    }
  };

  const handleDownloadGeneratedSlipPdf = async (allocation) => {
    try {
      const file = await downloadGeneratedAllocationSlipPdf(allocation.id);
      saveBlob(
        file,
        allocation.generatedSlipPdfFileName || `allocation-${allocation.id}-slip.pdf`
      );
    } catch (error) {
      message.error(error.message || "Slip PDF татаж чадсангүй");
    }
  };

  const handleViewGeneratedSampleFilledPdf = async (allocation) => {
    try {
      const file = await downloadGeneratedAllocationSampleFilledPdf(allocation.id);
      openBlobInNewTab(file);
    } catch (error) {
      message.error(error.message || "Sample filled PDF нээж чадсангүй");
    }
  };

  const handleDownloadGeneratedSampleFilledPdf = async (allocation) => {
    try {
      const file = await downloadGeneratedAllocationSampleFilledPdf(allocation.id);
      saveBlob(
        file,
        allocation.generatedSampleFilledPdfFileName || `allocation-${allocation.id}-sample-filled.pdf`
      );
    } catch (error) {
      message.error(error.message || "Sample filled PDF татаж чадсангүй");
    }
  };

  const refreshSelectedRecordFromList = async (recordId) => {
    const rows = await loadTransactions();
    const updated = (rows || []).find((x) => String(x.id) === String(recordId));

    if (updated) {
      setSelectedRecord(updated);
    }

    await refreshReturnsData();

    if (recordId) {
      await refreshDetails(recordId);
    }
  };

  const handleUploadInvoice = async (record, file) => {
    try {
      await uploadTransactionScheduleInvoice(record.id, file);
      message.success("Нэхэмжлэх PDF амжилттай нэмэгдлээ");
      await refreshSelectedRecordFromList(record.id);
    } catch (error) {
      message.error(error.message || "Нэхэмжлэх PDF нэмэж чадсангүй");
    }
  };

  const handleUploadContract = async (record, file) => {
    try {
      await uploadTransactionScheduleContract(record.id, file);
      message.success("Гэрээ PDF амжилттай нэмэгдлээ");
      await refreshSelectedRecordFromList(record.id);
    } catch (error) {
      message.error(error.message || "Гэрээ PDF нэмэж чадсангүй");
    }
  };

  const attachments = selectedRecord
    ? [
      {
        key: "invoice",
        title: "Нэхэмжлэх",
        fileName: detailData?.invoiceFileName || selectedRecord.invoiceFileName,
        onView:
          detailData?.invoiceFileName || selectedRecord?.invoiceFileName
            ? () => handleViewInvoice(selectedRecord)
            : undefined,
        onDownload:
          detailData?.invoiceFileName || selectedRecord?.invoiceFileName
            ? () => handleDownloadInvoice(selectedRecord)
            : undefined,
        onUpload:
          !(detailData?.invoiceFileName || selectedRecord?.invoiceFileName)
            ? (file) => handleUploadInvoice(selectedRecord, file)
            : undefined,
      },
      {
        key: "contract",
        title: "Гэрээ",
        fileName: detailData?.contractFileName || selectedRecord.contractFileName,
        onView:
          detailData?.contractFileName || selectedRecord?.contractFileName
            ? () => handleViewContract(selectedRecord)
            : undefined,
        onDownload:
          detailData?.contractFileName || selectedRecord?.contractFileName
            ? () => handleDownloadContract(selectedRecord)
            : undefined,
        onUpload:
          !(detailData?.contractFileName || selectedRecord?.contractFileName)
            ? (file) => handleUploadContract(selectedRecord, file)
            : undefined,
      },
    ]
    : [];

  const runAllChecksButton = (
    <Button loading={runningAllChecks} onClick={handleRunAllChecks}>
      Бүх шалгалт ажиллуулах
    </Button>
  );

  const scheduleTab = TransactionSchedulesTab({
    filteredTransactions,
    loadingTable,
    openManage,
    openDetails,
    canDelete,
    handleDeleteTransactionSchedule,
    openBulkPreview,
    onBulkAgree: handleBulkAgree,
    onViewInvoice: handleViewInvoice,
    onDownloadInvoice: handleDownloadInvoice,
    onViewContract: handleViewContract,
    onDownloadContract: handleDownloadContract,
  });

  const onTabChange = (activeKey) => {
    const params = new URLSearchParams(location.search);

    if (activeKey === "returns") {
      params.set("tab", "returns");
    } else {
      params.delete("tab");
    }

    navigate(`${ROUTE_PATHS.TRANSACTION_SCHEDULE}?${params.toString()}`, {
      replace: true,
    });
  };



  return (
    <div className="space-y-4">
      <Card bordered={false} className="shadow-sm rounded-2xl">
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={14}>
            <Space direction="vertical" size={4}>
              <Title level={2} style={{ margin: 0 }}>
                Гүйлгээний хуваарийн ажлын дэлгэц
              </Title>
              <Text type="secondary">
                Жагсаалт, буцаалт, шалгалт, удирдлага болон дэлгэрэнгүй мэдээллийг эндээс ажиллуулна.
              </Text>
            </Space>
          </Col>

          <Col xs={24} md="auto">
            <Space wrap>
              <Button onClick={() => navigate(ROUTE_PATHS.TRANSACTION_SCHEDULE_DASHBOARD)}>
                Хяналтын самбар
              </Button>
              {runAllChecksButton}
              <Button
                icon={<UploadOutlined />}
                onClick={() => setExcelImportOpen(true)}
              >
                Excel импорт
              </Button>
              <ManualTransactionScheduleForm onSaved={loadTransactions} />
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="shadow-sm rounded-2xl">
            <div><strong>Нийт хүсэлт:</strong> {summary.totalCount}</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="shadow-sm rounded-2xl">
            <div><strong>Хоосон:</strong> {summary.emptyCount}</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="shadow-sm rounded-2xl">
            <div><strong>Зөвшөөрөл хүлээж буй:</strong> {summary.waitingAgreeCount}</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="shadow-sm rounded-2xl">
            <div><strong>Буцаалт:</strong> {returns.length}</div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>

        <Col xs={24}>
          <TransactionScheduleFilters
            searchRequestNumber={searchRequestNumber}
            setSearchRequestNumber={setSearchRequestNumber}
            searchPayer={searchPayer}
            setSearchPayer={setSearchPayer}
            selectedParentDisplayStatus={selectedParentDisplayStatus}
            setSelectedParentDisplayStatus={setSelectedParentDisplayStatus}
            selectedCreatedAtRange={selectedCreatedAtRange}
            setSelectedCreatedAtRange={setSelectedCreatedAtRange}
            totalRows={filteredTransactions.length}
          />
        </Col>

        <Col xs={24}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              onTabChange(key);
            }}
            items={[
              {
                key: "schedules",
                label: `Гүйлгээний хуваариуд (${filteredTransactions.length})`,
                children: scheduleTab.tabNode,
              },
              {
                key: "returns",
                label: (
                  <span
                    className={returns.length > 0 ? "ts-pulse" : ""}
                    style={{
                      color: returns.length > 0 ? "#cf1322" : undefined,
                      fontWeight: 600,
                      display: "inline-block",
                    }}
                  >
                    Буцаалтууд ({returns.length})
                  </span>
                ),
                children: (
                  <UnmatchedReturnsTab
                    unmatchedReturns={returns}
                    loadingUnmatchedReturns={loadingUnmatchedReturns}
                    ignoredReturns={ignoredReturns}
                    loadingIgnoredReturns={loadingIgnoredReturns}
                    returnHistory={returnHistory}
                    loadingReturnHistory={loadingReturnHistory}
                    loadTransactions={loadTransactions}
                    loadUnmatchedReturns={loadUnmatchedReturns}
                    loadIgnoredReturns={loadIgnoredReturns}
                    selectedRecord={selectedRecord}
                    refreshDetails={refreshDetails}
                    runAllChecksButton={runAllChecksButton}
                  />
                ),
              },
            ]}
          />
        </Col>
        <Col xs={24}>
          <TransactionScheduleCalendarCard
            filteredTransactions={filteredTransactions}
            returns={returns}
            returnHistory={returnHistory}
            runAllChecksButton={runAllChecksButton}
            onOpenRecord={openDetails}
            onManageRecord={openManage}
            canDelete={canDelete}
            onDeleteRecord={handleDeleteTransactionSchedule}
            selectedRecord={selectedRecord}
            refreshDetails={refreshDetails}
            loadTransactions={loadTransactions}
            loadUnmatchedReturns={loadUnmatchedReturns}
            loadIgnoredReturns={loadIgnoredReturns}
            refreshReturnHistory={loadReturnHistory}
            onViewInvoice={handleViewInvoice}
            onDownloadInvoice={handleDownloadInvoice}
            onViewContract={handleViewContract}
            onDownloadContract={handleDownloadContract}
          />
        </Col>
      </Row>

      <Modal
        title="Excel файлаар импорт хийх"
        open={excelImportOpen}
        onCancel={() => setExcelImportOpen(false)}
        footer={null}
        width={1100}
        destroyOnClose
      >
        <UploadTransactionScheduleExcel
          inModal
          onImportSuccess={async () => {
            setExcelImportOpen(false);
            await loadTransactions();
            await refreshReturnsData();
          }}
        />
      </Modal>

      <TransactionScheduleDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedRecord={selectedRecord}
        detailData={detailData}
        statusLogs={statusLogs}
        loading={loadingDetails}
        onManage={(record) => {
          setDrawerOpen(false);
          openManage(record);
        }}
        attachments={attachments}
        onViewGeneratedSlipPdf={handleViewGeneratedSlipPdf}
        onDownloadGeneratedSlipPdf={handleDownloadGeneratedSlipPdf}
        onViewGeneratedSampleFilledPdf={handleViewGeneratedSampleFilledPdf}
        onDownloadGeneratedSampleFilledPdf={handleDownloadGeneratedSampleFilledPdf}
      />

      <TransactionScheduleManageModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        record={manageRecord}
        onSaved={async () => {
          await loadTransactions();
          await refreshReturnsData();

          const refreshId = selectedRecord?.id || manageRecord?.id;
          if (refreshId) {
            await refreshDetails(refreshId);
          }
        }}
        canAgree={canAgree}
        canUnagree={canUnagree}
        onAgreeAllocation={handleAgreeAllocation}
        onUnagreeAllocation={handleUnagreeAllocation}
        onRunAllocationCheck={handleRunAllocationCheck}
        runningCheckIds={runningCheckIds}
        agreeingAllocationIds={agreeingAllocationIds}
        unagreeingAllocationIds={unagreeingAllocationIds}
      />

      <BulkManagePreviewModal
        open={bulkPreviewOpen}
        rows={bulkRows}
        value={bulkPayload}
        onChange={setBulkPayload}
        onCancel={() => setBulkPreviewOpen(false)}
        onConfirm={handleBulkManage}
      />

      <BatchProgressModal
        open={batch.open}
        title={batch.title}
        items={batch.items}
        onClose={batch.close}
      />
    </div>
  );
}

export default TransactionSchedulePage;